import { Row, Typography } from "antd";
import { useMemo, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import InfluencerCard from "pages/influencers/InfluencerCard";
import CustomSelect from "components/custom-select/CustomSelect";
import { PlayCircleOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
// import InfluencerModal from "components/influencer-modal";
import { BusinessState } from "redux-context/business";
import { useSelector } from "react-redux";
import { Venue } from "interfaces/venue";

export const parseHourTo24 = (hourRaw?: string) => {
  if (!hourRaw) return "00:00:00";
  const s = hourRaw.trim().toUpperCase();

  // Check for AM/PM
  const ampmMatch = s.match(/\b(AM|PM)\b/);
  if (ampmMatch) {
    // Remove AM/PM and parse hour/minute
    const timePart = s.replace(/\s*(AM|PM)\b/, "");
    const hm = timePart.match(/^(\d{1,2})(?::(\d{2}))?/);
    if (!hm) return "00:00:00";
    let hour = parseInt(hm[1], 10);
    const minute = hm[2] || "00";
    const isPM = ampmMatch[1] === "PM";

    if (hour === 12) {
      hour = isPM ? 12 : 0;
    } else if (isPM) {
      hour += 12;
    }

    return `${hour.toString().padStart(2, "0")}:${minute}:00`;
  }

  // Assume already 24h like "12:00" or "7:30"
  const hm = s.match(/^(\d{1,2})(?::(\d{2}))?/);
  if (!hm) return "00:00:00";
  const hour = hm[1].padStart(2, "0");
  const minute = hm[2] || "00";
  return `${hour}:${minute}:00`;
};

const Applications: React.FC = () => {
  const pendingInfluencers = useLoaderData() as any;
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  // const [selectedFoodie, setSelectedFoodie] = useState<string>("");
  const campaignIdFilter = queryParams.get("campaignId") || "all";
  const statusFilter = queryParams.get("status") || "pending";
  // const [openFoodieModal, setOpenFoodieModal] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [selectedCampaign, setSelectedCampaign] =
    useState<string>(campaignIdFilter);

  const { business } = useSelector((state: BusinessState) => state.business);

  const venueOptions = useMemo(() => {
    if (business) {
      return (
        business.venues?.map((venue: Venue) => ({
          value: venue.uuid,
          label: `${venue.name} - ${venue.addressDisplay}`,
        })) || []
      );
    }
  }, [business]);

  const getSortComparator = (status: string) => {
    if (status === "pending") {
      return (a: any, b: any) =>
        new Date(b.dueDateToReview).getTime() -
        new Date(a.dueDateToReview).getTime();
    }

    if (status === "accepted") {
      const getBookingTimestamp = (item: any) => {
        const date = item?.applicationDate?.date;
        const hour = item?.applicationDate?.hour;
        if (!date) return 0;

        const timeStr = parseHourTo24(hour);
        const datePart = date.includes("T") ? date.split("T")[0] : date;

        const dateTimeStr = hour ? `${datePart}T${timeStr}Z` : date;
        const ts = new Date(dateTimeStr).getTime();
        return isNaN(ts) ? 0 : ts;
      };

      return (a: any, b: any) =>
        getBookingTimestamp(a) - getBookingTimestamp(b);
    }

    return (a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

  const influencerComparator = useMemo(
    () => getSortComparator(statusFilter),
    [statusFilter]
  );

  const campaignFilterOptions = useMemo(() => {
    return [
      { value: "all", label: "All Campaigns" },
      ...pendingInfluencers
        .map((collab: any) => ({
          value: collab.campaign?.id,
          label: collab.campaign?.name,
        }))
        .filter((item: any) => item.value)
        .filter(
          (item: any, index: number, self: any) =>
            index === self.findIndex((t: any) => t.value === item.value)
        ),
    ];
  }, [pendingInfluencers]);

  const filteredInfluencers = useMemo(() => {
    const selected = Array.isArray(selectedLocation)
      ? selectedLocation.filter(Boolean)
      : selectedLocation
      ? [selectedLocation]
      : [];

    const isAllLocations = selected.length === 0 || selected.includes("all");
    const isAllCampaigns = selectedCampaign === "all";

    const influencersFiltered = pendingInfluencers.filter((collab: any) => {
      const isMatchingVenue = selected.includes(collab?.venue?.uuid);
      const isMatchingCampaign = collab.campaign?.id === selectedCampaign;

      if (
        (isAllLocations || isMatchingVenue) &&
        (isAllCampaigns || isMatchingCampaign)
      ) {
        return true;
      }

      return false;
    });

    return influencersFiltered.sort(influencerComparator);
  }, [
    pendingInfluencers,
    selectedLocation,
    selectedCampaign,
    influencerComparator,
  ]);

  const onClickFoodie = (foodieId: string) => {
    navigate(`/creators/${foodieId}`);
  };

  const onChangeStatusFilter = (value: string) => {
    navigate(`/applications?status=${value}`);
    setSelectedCampaign("all");
  };

  return (
    <div className="applications influencers">
      <Row justify="space-between" style={{ marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 24,
          }}
        >
          <CustomSelect
            icon={<PlayCircleOutlined style={{ fontSize: 16 }} />}
            value={selectedCampaign}
            options={campaignFilterOptions}
            onChange={(value) => setSelectedCampaign(value)}
            popupMatchSelectWidth={false}
            displayLabel="Campaign"
          />
          <CustomSelect
            value={selectedLocation}
            options={venueOptions}
            icon={<FontAwesomeIcon icon={faLocationDot} />}
            onChange={(value) => setSelectedLocation(value)}
            popupMatchSelectWidth={false}
            displayLabel="Location"
            mode="multiple"
            showSelectedAsTags
          />

          <CustomSelect
            value={statusFilter}
            options={[
              { value: "pending", label: "Pending" },
              { value: "accepted", label: "Booked" },
              { value: "expired", label: "Expired" },
            ]}
            onChange={onChangeStatusFilter}
            popupMatchSelectWidth={false}
            displayLabel="Status"
          />
        </div>
      </Row>
      <Row style={{ marginBottom: 40 }}>
        <Typography.Text>
          {filteredInfluencers.length} application
          {filteredInfluencers.length !== 1 ? "s" : ""} found
        </Typography.Text>
        {/* {(() => {
          const appliedItems: { key: string; label: string; value: any }[] = [];

          if (statusFilter) {
            appliedItems.push({
              key: "status",
              label: "Status",
              value: statusFilter,
            });
          }

          if (selectedCampaign !== "all") {
            const campaignLabel =
              campaignFilterOptions.find(
                (c: any) => c.value === selectedCampaign
              )?.label || selectedCampaign;
            appliedItems.push({
              key: "campaign",
              label: "Campaign",
              value: campaignLabel,
            });
          }

          if (Array.isArray(selectedLocation) && selectedLocation.length > 0) {
            const locLabelsArr =
              (venueOptions || [])
                .filter((v: any) => selectedLocation.includes(v.value))
                .map((v: any) => v.label) || [];
            appliedItems.push({
              key: "location",
              label: "Location",
              value: locLabelsArr.length ? locLabelsArr : "Selected",
            });
          }

          if (appliedItems.length === 0) return null;

          return (
            <ul style={{ margin: 0 }}>
              {appliedItems.map((item) => (
                <li key={item.key} style={{ whiteSpace: "pre-line" }}>
                  <strong>{item.label}:</strong>{" "}
                  {Array.isArray(item.value)
                    ? item.value.map((val: string, idx: number) => (
                        <span key={idx}>
                          {val}
                          {idx !== item.value.length - 1 && <br />}
                        </span>
                      ))
                    : item.value}
                </li>
              ))}
            </ul>
          );
        })()} */}
      </Row>
      <Row gutter={[16, 16]} style={{ gap: 16 }}>
        {filteredInfluencers.map((influencer: any) => (
          <InfluencerCard
            influencer={influencer}
            onInfluencerClick={() => onClickFoodie(influencer.foodieId)}
            key={`INFLUENCER_${influencer.foodieId}_${
              influencer.collabId
            }_${Math.random().toString(36).substring(2, 9)}`}
          />
        ))}
      </Row>
    </div>
  );
};

export default Applications;
